import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader, EmptyState, Confirm } from "../components/Shared";
import { fmtFCFA, ROLES } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Pencil, Trash2, Eye, Users, AlertTriangle, Building2 } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../components/ui/hover-card";
import { toast } from "sonner";
import { TableSkeleton } from "../components/Skeletons";
import { EditableListSelect } from "../components/EditableListSelect";
import PhoneField from "../components/PhoneField";
import { isValidEmail, isValidPhone } from "../lib/validate";

const empty = { name: "", email: "", phone: "", gender: "Male", profession: "", category: "", staff_category: "", subcontractor_id: "", role: "", company_ids: [] };
const isSubEmployee = (v) => /^\s*sub[-\s]?employee\s*$/i.test(v || "");
const F = ({ label, required, children }) => (
  <div className="space-y-1.5"><Label className="text-sm">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</Label>{children}</div>
);

export default function Staff() {
  const { isRole } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [viewing, setViewing] = useState(null);
  const [warn, setWarn] = useState(null);
  const [openSub, setOpenSub] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [unitMove, setUnitMove] = useState(null);
  const [moveTarget, setMoveTarget] = useState("");
  const [moving, setMoving] = useState(false);
  const [unitChoice, setUnitChoice] = useState("");
  const canManage = isRole(ROLES.CEO);

  const { data: rows = [], isLoading } = useQuery({ queryKey: ["employees"], queryFn: () => api.get("/employees").then((r) => r.data) });
  const { data: companies = [] } = useQuery({ queryKey: ["companies"], queryFn: () => api.get("/companies").then((r) => r.data) });
  const load = () => qc.invalidateQueries();

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "—";
  const openUnitMove = (e) => { setUnitMove(e); setMoveTarget(""); };
  const doUnitAssign = async (mode) => {
    if (!moveTarget) { toast.error("Select a business unit first."); return; }
    setMoving(true);
    try {
      await api.post(`/employees/${unitMove.id}/assign-unit`, { target_company_id: moveTarget, mode });
      toast.success(mode === "copy" ? "Employee shared to the unit." : "Employee moved to the unit.");
      setUnitMove(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update unit."); }
    finally { setMoving(false); }
  };

  const { data: meta = null } = useQuery({ queryKey: ["meta-config"], queryFn: () => api.get("/meta/config").then((r) => r.data) });
  const roleOptions = meta?.roles || [];

  const { data: rollup = null } = useQuery({
    queryKey: ["sc-rollup", openSub],
    enabled: !!openSub,
    queryFn: () => api.get(`/subcontractors/${openSub}/rollup`).then((r) => r.data),
  });

  const statusOptions = Array.from(new Set(rows.map((e) => e.status).filter(Boolean)));
  const displayRows = statusFilter === "all" ? rows : rows.filter((e) => (e.status || "Active") === statusFilter);
  const setEmployeeRole = async (e, role) => {
    try {
      await api.put(`/employees/${e.id}`, { ...e, role });
      qc.invalidateQueries({ queryKey: ["employees"] });
      toast.success("Role updated");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openNew = () => {
    setEditing(null); setErrors({}); setForm(empty); setOpen(true);
    const cur = localStorage.getItem("stems_company") || "";
    setUnitChoice(cur || (companies[0]?.id || "all"));
  };
  const openEdit = (e) => { setEditing(e); setErrors({}); setForm({ ...empty, ...e, gender: e.gender || "Male" }); setOpen(true); };

  const doSave = async (payload) => {
    try {
      if (editing) await api.put(`/employees/${editing.id}`, payload);
      else await api.post("/employees", payload);
      toast.success(editing ? "Employee updated" : "Employee added");
      setOpen(false); setWarn(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const save = async () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required.";
    if (!form.email.trim()) errs.email = "Email is required.";
    else if (!isValidEmail(form.email)) errs.email = "Enter a valid email address.";
    if (!form.phone) errs.phone = "Telephone is required.";
    else if (!isValidPhone(form.phone)) errs.phone = "Enter a valid phone number.";
    if (!form.gender) errs.gender = "Gender is required.";
    if (!form.profession) errs.profession = "Profession is required.";
    if (!isSubEmployee(form.staff_category) && !form.category) errs.category = "Designation is required.";
    if (isSubEmployee(form.staff_category) && !form.subcontractor_id) errs.subcontractor_id = "Select the sub contractor this sub-employee works under.";
    if (Object.keys(errs).length) { setErrors(errs); toast.error("Please complete all required fields."); return; }
    setErrors({});
    const payload = { ...(editing || {}), ...form };
    if (!editing) {
      if (!unitChoice) { setErrors({ unit: "Select a business unit." }); toast.error("Select a business unit for the employee."); return; }
      payload.company_ids = unitChoice === "all" ? companies.map((c) => c.id) : [unitChoice];
    }
    if (isSubEmployee(form.staff_category) && form.subcontractor_id) {
      const sub = rows.find((r) => r.id === form.subcontractor_id);
      if (!sub) { setWarn({ payload, reason: "The selected sub contractor no longer exists — it may have been deleted." }); return; }
      if ((sub.status || "Active") !== "Active") { setWarn({ payload, reason: `Sub contractor "${sub.name}" is not active (status: ${sub.status || "—"}).` }); return; }
    }
    doSave(payload);
  };
  const remove = async (e) => {
    try { await api.delete(`/employees/${e.id}`); toast.success("Employee deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Employees" subtitle="Staff records — name, contact, role and salary">
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-40" data-testid="staff-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {statusOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          {canManage && <Button data-testid="staff-add-button" onClick={openNew} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"><Plus className="h-4 w-4 mr-2" /> Add Employee</Button>}
        </div>
      </PageHeader>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {isLoading ? <TableSkeleton cols={10} /> : rows.length === 0 ? (
          <EmptyState text="No employees yet." action={canManage ? <Button data-testid="staff-empty-add" onClick={openNew} className="bg-[#1E3A8A] hover:bg-[#152C6F]">Add your first employee</Button> : null} />
        ) : displayRows.length === 0 ? (
          <EmptyState text={`No employees with status "${statusFilter}".`} />
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Telephone</TableHead>
              <TableHead>Gender</TableHead><TableHead>Profession</TableHead><TableHead>Designation</TableHead><TableHead>Category</TableHead><TableHead>Role</TableHead>
              <TableHead className="text-right">Salary</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {displayRows.map((e) => (
                <TableRow key={e.id} data-testid={`staff-row-${e.id}`}>
                  <TableCell className="font-medium text-slate-800">
                    <div>{e.name}</div>
                    <div className="flex flex-wrap gap-1 mt-1" data-testid={`staff-units-${e.id}`}>
                      {(e.company_ids && e.company_ids.length ? e.company_ids : (e.company_id ? [e.company_id] : [])).map((cid) => (
                        <span key={cid} className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-100 whitespace-nowrap">{companyName(cid)}</span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-600">{e.email || "—"}</TableCell>
                  <TableCell className="text-slate-600">{e.phone || "—"}</TableCell>
                  <TableCell className="text-slate-600">{e.gender || "—"}</TableCell>
                  <TableCell className="text-slate-600">{e.profession || "—"}</TableCell>
                  <TableCell className="text-slate-600">{e.category || "—"}</TableCell>
                  <TableCell className="text-slate-600">
                    {(() => {
                      const isSub = /^\s*sub[-\s]?contractor\s*$/i.test(e.staff_category || "");
                      if (!isSub) return e.staff_category || "—";
                      const subs = rows.filter((r) => r.subcontractor_id === e.id);
                      return (
                        <HoverCard openDelay={100} closeDelay={80} onOpenChange={(o) => setOpenSub(o ? e.id : null)}>
                          <HoverCardTrigger asChild>
                            <button type="button" data-testid={`staff-rollup-trigger-${e.id}`} className="inline-flex items-center gap-1.5 text-slate-700 hover:text-[#1E3A8A] underline decoration-dotted underline-offset-2">
                              {e.staff_category}
                              {subs.length > 0 && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
                                  <Users className="h-3 w-3" />{subs.length}
                                </span>
                              )}
                            </button>
                          </HoverCardTrigger>
                          <HoverCardContent align="start" className="w-80" data-testid={`staff-rollup-card-${e.id}`}>
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Sub-employees under {e.name}</p>
                            {subs.length === 0 ? (
                              <p className="text-sm text-slate-500">No sub-employees linked yet.</p>
                            ) : (
                              <>
                                <ul className="space-y-1.5">
                                  {subs.map((s) => {
                                    const r = openSub === e.id ? (rollup?.employees || []).find((x) => x.id === s.id) : null;
                                    return (
                                      <li key={s.id} className="text-sm" data-testid={`staff-rollup-sub-${s.id}`}>
                                        <div className="flex items-center justify-between">
                                          <span className="font-medium text-slate-800">{s.name}</span>
                                          <span className="text-slate-500 text-xs">{s.profession || "—"}</span>
                                        </div>
                                        {r && (
                                          <div className="flex items-center justify-between text-xs text-slate-500 mt-0.5">
                                            <span>Contract {fmtFCFA(r.amount)}</span>
                                            <span>Advance {fmtFCFA(r.advance)}</span>
                                          </div>
                                        )}
                                      </li>
                                    );
                                  })}
                                </ul>
                                {openSub === e.id && rollup?.totals && (
                                  <div className="mt-2 pt-2 border-t border-slate-100 space-y-1" data-testid={`staff-rollup-totals-${e.id}`}>
                                    <div className="flex items-center justify-between text-xs"><span className="text-slate-500">Total contract</span><span className="font-mono-num font-medium text-slate-800">{fmtFCFA(rollup.totals.amount)}</span></div>
                                    <div className="flex items-center justify-between text-xs"><span className="text-slate-500">Total advance</span><span className="font-mono-num font-medium text-slate-800">{fmtFCFA(rollup.totals.advance)}</span></div>
                                    <div className="flex items-center justify-between text-xs"><span className="text-slate-500">Balance</span><span className="font-mono-num font-semibold text-[#1E3A8A]">{fmtFCFA(rollup.totals.balance)}</span></div>
                                  </div>
                                )}
                              </>
                            )}
                          </HoverCardContent>
                        </HoverCard>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {canManage ? (
                      <Select value={e.role || ""} onValueChange={(v) => setEmployeeRole(e, v)}>
                        <SelectTrigger className="h-8 w-40" data-testid={`role-select-${e.id}`}><SelectValue placeholder="Assign role" /></SelectTrigger>
                        <SelectContent>{roleOptions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (e.role || "—")}
                  </TableCell>
                  <TableCell className="text-right font-mono-num">{fmtFCFA(e.salary_total ?? e.salary)}</TableCell>
                  <TableCell className="text-right">
                    <div className="stems-actions">
                      <Button size="icon" variant="ghost" data-testid={`staff-view-${e.id}`} onClick={() => setViewing(e)}><Eye className="h-4 w-4" /></Button>
                      {canManage && <Button size="icon" variant="ghost" data-testid={`staff-move-${e.id}`} title="Move or copy to a business unit" onClick={() => openUnitMove(e)}><Building2 className="h-4 w-4" /></Button>}
                      {canManage && <Button size="icon" variant="ghost" data-testid={`staff-edit-${e.id}`} onClick={() => openEdit(e)}><Pencil className="h-4 w-4" /></Button>}
                      {canManage && <Confirm onConfirm={() => remove(e)} title="Delete employee?" description={`This removes "${e.name}".`}
                        trigger={<Button size="icon" variant="ghost" data-testid={`staff-delete-${e.id}`} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>} />}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Employee" : "Add Employee"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <F label="Name" required>
              <Input data-testid="staff-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={errors.name ? "border-red-400" : ""} />
              {errors.name && <p className="text-xs text-red-600" data-testid="staff-name-error">{errors.name}</p>}
            </F>
            <F label="Email" required>
              <Input data-testid="staff-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={errors.email ? "border-red-400" : ""} />
              {errors.email && <p className="text-xs text-red-600" data-testid="staff-email-error">{errors.email}</p>}
            </F>
            <F label="Telephone" required>
              <PhoneField value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="staff-phone" invalid={!!errors.phone} />
              {errors.phone && <p className="text-xs text-red-600" data-testid="staff-phone-error">{errors.phone}</p>}
            </F>
            <F label="Gender" required>
              <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                <SelectTrigger data-testid="staff-gender"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
              </Select>
            </F>
            <F label="Profession" required>
              <EditableListSelect value={form.profession || ""} onValueChange={(v) => setForm({ ...form, profession: v })}
                endpoint="/professions" noun="profession" canManage={canManage} placeholder="Select profession" testPrefix="staff-profession" />
              {errors.profession && <p className="text-xs text-red-600" data-testid="staff-profession-error">{errors.profession}</p>}
            </F>
            <F label="Category">
              <EditableListSelect value={form.staff_category || ""} onValueChange={(v) => setForm({ ...form, staff_category: v })}
                endpoint="/staff-categories" noun="category" canManage={canManage} placeholder="Select category" testPrefix="staff-staffcat" />
            </F>
            {isSubEmployee(form.staff_category) && (
              <F label="Sub Contractor" required>
                <Select value={form.subcontractor_id || ""} onValueChange={(v) => setForm({ ...form, subcontractor_id: v })}>
                  <SelectTrigger data-testid="staff-subcontractor" className={errors.subcontractor_id ? "border-red-400" : ""}><SelectValue placeholder="Select sub contractor" /></SelectTrigger>
                  <SelectContent>
                    {rows.filter((r) => /^\s*sub[-\s]?contractor\s*$/i.test(r.staff_category || "") && r.id !== editing?.id).map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {errors.subcontractor_id && <p className="text-xs text-red-600" data-testid="staff-subcontractor-error">{errors.subcontractor_id}</p>}
              </F>
            )}
            <F label="Designation" required>
              <EditableListSelect value={form.category || ""} onValueChange={(v) => setForm({ ...form, category: v })}
                endpoint="/employee-categories" noun="designation" canManage={canManage} placeholder="Select designation" testPrefix="staff-category" />
              {errors.category && <p className="text-xs text-red-600" data-testid="staff-category-error">{errors.category}</p>}
            </F>
            <F label="Role">
              <Select value={form.role || ""} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="staff-form-role-select"><SelectValue placeholder="Assign role" /></SelectTrigger>
                <SelectContent>{roleOptions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            {!editing && (
              <F label="Business unit" required>
                <Select value={unitChoice} onValueChange={setUnitChoice}>
                  <SelectTrigger data-testid="staff-add-unit" className={errors.unit ? "border-red-400" : ""}><SelectValue placeholder="Select unit" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All business units</SelectItem>
                    {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {errors.unit && <p className="text-xs text-red-600" data-testid="staff-unit-error">{errors.unit}</p>}
              </F>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="staff-form-submit" onClick={save} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">{editing ? "Save Changes" : "Add Employee"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewing} onOpenChange={(o) => { if (!o) setViewing(null); }}>
        <DialogContent className="max-w-lg" data-testid="staff-view-dialog">
          <DialogHeader><DialogTitle>{viewing?.name}</DialogTitle></DialogHeader>
          {viewing && (
            <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
              <VRow label="Email">{viewing.email || "—"}</VRow>
              <VRow label="Telephone">{viewing.phone || "—"}</VRow>
              <VRow label="Gender">{viewing.gender || "—"}</VRow>
              <VRow label="Profession">{viewing.profession || "—"}</VRow>
              <VRow label="Designation">{viewing.category || "—"}</VRow>
              <VRow label="Category">{viewing.staff_category || "—"}</VRow>
              <VRow label="Role">{viewing.role || "—"}</VRow>
              <VRow label="Salary">{fmtFCFA(viewing.salary_total ?? viewing.salary)}</VRow>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewing(null)} data-testid="staff-view-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!warn} onOpenChange={(o) => { if (!o) setWarn(null); }}>
        <DialogContent className="max-w-md" data-testid="staff-warn-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700"><AlertTriangle className="h-5 w-5" /> Sub contractor issue</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">{warn?.reason} Do you still want to save this employee?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWarn(null)} data-testid="staff-warn-cancel">Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" data-testid="staff-warn-proceed" onClick={() => doSave(warn.payload)}>Save anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!unitMove} onOpenChange={(o) => { if (!o) setUnitMove(null); }}>
        <DialogContent className="max-w-md" data-testid="staff-unit-dialog">
          <DialogHeader><DialogTitle>Move or copy to a business unit</DialogTitle></DialogHeader>
          {unitMove && (
            <div className="space-y-4">
              <div className="text-sm text-slate-600">
                <span className="font-medium text-slate-800">{unitMove.name}</span> is currently in{" "}
                <span className="font-medium text-slate-800">{companyName(unitMove.company_id)}</span>.
              </div>
              <F label="Destination business unit" required>
                <Select value={moveTarget} onValueChange={setMoveTarget}>
                  <SelectTrigger data-testid="staff-unit-target"><SelectValue placeholder="Select a unit" /></SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
              <p className="text-xs text-slate-500">
                <b>Move</b> relocates the employee so they appear only in the destination unit.{" "}
                <b>Copy</b> keeps them in their current unit and also shows them in the destination unit.
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setUnitMove(null)} data-testid="staff-unit-cancel">Cancel</Button>
            <Button variant="outline" disabled={moving || !moveTarget} data-testid="staff-unit-copy" onClick={() => doUnitAssign("copy")}>Copy here</Button>
            <Button className="bg-[#1E3A8A] hover:bg-[#1D4ED8]" disabled={moving || !moveTarget} data-testid="staff-unit-move" onClick={() => doUnitAssign("move")}>Move here</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const VRow = ({ label, children }) => (
  <div className="flex items-center justify-between px-4 py-2.5 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="font-medium text-slate-800 text-right">{children}</span>
  </div>
);
